import React, { useState, useMemo } from 'react';
import { Search, Calendar, Users, ClipboardList, Clock, ArrowRight, Loader2 } from 'lucide-react';
import { scheduleData as importedScheduleData } from '../SchoolSyncVA/data/scheduleData';

const dayOrder = {
    'Segunda-feira': 1,
    'Terça-feira': 2,
    'Quarta-feira': 3,
    'Quinta-feira': 4,
    'Sexta-feira': 5,
    'Dia de Teste': 6
};

const turmas = ['6A', '6B', '6C', '6D', '7A', '7B', '7C', '8A', '8B', '8C', '8D', '9A', '9B', '9C', '9D'];
const weekdays = ['Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira'];
const turmas67 = ['6A', '6B', '6C', '6D', '7A', '7B', '7C'];
const turmas89 = ['8A', '8B', '8C', '8D', '9A', '9B', '9C', '9D'];

export default function CommunityTimePanel() {
    const [selectedProfessor, setSelectedProfessor] = useState('');

    const [scheduleData, setScheduleData] = useState(importedScheduleData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Helper redefinido localmente para ter acesso ao state
    const getProfessor = (type, day, turma) => {
        const entry = scheduleData.find(s => s.type === type && (day ? s.day === day : true) && s.turma === turma);
        return entry ? entry.professor : '-';
    };

    // Obter lista única e ordenada de professores, removendo interrogações se houverem
    const uniqueProfessors = useMemo(() => {
        const profs = new Set(scheduleData.map(item => item.professor));
        profs.delete('?');
        profs.delete('-');
        profs.delete('');
        return Array.from(profs).sort();
    }, [scheduleData]);

    // Filtrar o cronograma com base no professor selecionado
    const professorSchedule = useMemo(() => {
        if (!selectedProfessor) return { community: [], cycle: [] };

        const filtered = scheduleData.filter(item => item.professor === selectedProfessor);

        // Ordenar logicamente pelos dias da semana
        filtered.sort((a, b) => dayOrder[a.day] - dayOrder[b.day]);

        return {
            community: filtered.filter(item => item.type === 'Community Time'),
            cycle: filtered.filter(item => item.type === 'Cycle Tests')
        };
    }, [selectedProfessor]);

    return (
        <div className="min-h-[calc(100vh-64px)] bg-slate-50 text-slate-800 font-sans pb-12 w-full">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40 w-full rounded-t-xl overflow-hidden mt-6">
                <div className="w-full mx-auto px-4 py-6 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-600 p-2.5 rounded-xl text-white shadow-md">
                            <ClipboardList className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight">Painel do Professor - Community Time & Cycle</h1>
                            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-0.5">Encontre sua turma e horários semanais</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="w-full mx-auto px-4 sm:px-6 lg:px-8 pt-8">

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mb-4" />
                        <p className="text-slate-500 font-medium">Carregando horários direto do Google Sheets...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-16 bg-rose-50 rounded-3xl border border-rose-200 shadow-sm">
                        <div className="mx-auto w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4">
                            <Clock className="w-8 h-8 text-rose-500" />
                        </div>
                        <h3 className="mt-2 text-lg font-semibold text-rose-900">Falha ao carregar</h3>
                        <p className="mt-1 text-rose-600 max-w-md mx-auto">{error}. Verifique o acesso da planilha.</p>
                    </div>
                ) : (
                    <>
                        {/* Search/Filter Box */}
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 mb-8 relative z-0">
                            <label htmlFor="professor-select" className="block text-sm font-semibold text-slate-700 mb-2">
                                Selecione o seu nome
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <Search className="h-5 w-5 text-indigo-400" />
                                </div>
                                <select
                                    id="professor-select"
                                    className="block w-full pl-12 pr-10 py-4 text-base border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer appearance-none shadow-inner"
                                    value={selectedProfessor}
                                    onChange={(e) => setSelectedProfessor(e.target.value)}
                                >
                                    <option value="" disabled>Toque para escolher na lista...</option>
                                    {uniqueProfessors.map(prof => (
                                        <option key={prof} value={prof}>{prof}</option>
                                    ))}
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                                    <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        {/* Results Area */}
                        {!selectedProfessor ? (
                            <div className="text-center py-16 bg-white rounded-3xl border border-slate-100 shadow-sm border-dashed">
                                <div className="mx-auto w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
                                    <Users className="w-10 h-10 text-indigo-400" />
                                </div>
                                <h3 className="mt-2 text-lg font-semibold text-slate-900">Nenhum professor selecionado</h3>
                                <p className="mt-1 text-slate-500 max-w-sm mx-auto">Selecione seu nome na lista acima para ver em qual turma você deve estar em cada dia da semana.</p>
                            </div>
                        ) : (
                            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

                                {/* Community Time Section */}
                                {professorSchedule.community.length > 0 && (
                                    <section>
                                        <div className="flex items-center gap-2 mb-4 px-1">
                                            <Clock className="w-5 h-5 text-emerald-600" />
                                            <h2 className="text-xl font-bold text-slate-800">Community Time</h2>
                                            <span className="ml-auto bg-emerald-100 text-emerald-700 py-1 px-3 rounded-full text-xs font-bold tracking-wide">
                                                SEMANAL
                                            </span>
                                        </div>

                                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                                            {professorSchedule.community.map((item, index) => (
                                                <div key={`comm-${index}`} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow relative group">
                                                    <div className="absolute top-0 left-0 w-1.5 h-full bg-emerald-400"></div>
                                                    <div className="p-5 pl-6">
                                                        <div className="flex justify-between items-start mb-3">
                                                            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider line-clamp-1" title={item.day}>{item.day}</p>
                                                            <div className="bg-slate-100 p-1.5 rounded-lg group-hover:bg-indigo-50 transition-colors">
                                                                <Calendar className="w-4 h-4 text-slate-400 group-hover:text-indigo-500" />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-3xl font-black text-slate-800 tracking-tight">
                                                                {item.turma}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* Cycle Tests Section */}
                                {professorSchedule.cycle.length > 0 && (
                                    <section className="pt-4">
                                        <div className="flex items-center gap-2 mb-4 px-1">
                                            <ClipboardList className="w-5 h-5 text-rose-600" />
                                            <h2 className="text-xl font-bold text-slate-800">Cycle Tests</h2>
                                            <span className="ml-auto bg-rose-100 text-rose-700 py-1 px-3 rounded-full text-xs font-bold tracking-wide">
                                                AVALIAÇÕES
                                            </span>
                                        </div>

                                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                            {professorSchedule.cycle.map((item, index) => (
                                                <div key={`cycle-${index}`} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow relative group">
                                                    <div className="absolute top-0 left-0 w-1.5 h-full bg-rose-400"></div>
                                                    <div className="p-5 pl-6">
                                                        <div className="flex justify-between items-start mb-3">
                                                            <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{item.day}</p>
                                                            <div className="bg-slate-100 p-1.5 rounded-lg group-hover:bg-rose-50 transition-colors">
                                                                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-rose-500" />
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-3xl font-black text-slate-800 tracking-tight">
                                                                {item.turma}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {/* Empty State se o professor não tiver nada agendado */}
                                {professorSchedule.community.length === 0 && professorSchedule.cycle.length === 0 && (
                                    <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-sm border-dashed">
                                        <div className="mx-auto w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                            <Clock className="w-8 h-8 text-slate-400" />
                                        </div>
                                        <p className="text-slate-500 font-medium">Não há horários agendados para você nas tabelas fornecidas.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Planilhas Completas */}
                        <div className="mt-16 border-t border-slate-200 pt-12 space-y-12">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-slate-900">Visão Geral das Tabelas</h2>
                                <p className="text-slate-500 mt-2 text-sm sm:text-base">Consulte os quadros completos de toda a coordenação. <br className="hidden sm:block" /> Seu nome aparecerá destacado se estiver selecionado acima.</p>
                            </div>

                            {/* Community Time Table */}
                            <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                <div className="bg-emerald-600 px-6 py-4">
                                    <h3 className="text-sm sm:text-lg font-bold text-white flex items-center gap-2">
                                        <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                                        Community Time (Segunda a Sexta)
                                    </h3>
                                </div>
                                <div className="overflow-x-auto pb-2">
                                    <table className="w-full text-sm text-left">
                                        <thead className="text-xs text-slate-600 uppercase bg-slate-50 border-b border-slate-200">
                                            <tr>
                                                <th className="px-6 py-4 font-bold text-center border-r border-slate-200 sticky left-0 bg-slate-50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Turma</th>
                                                {weekdays.map(day => (
                                                    <th key={day} className="px-6 py-4 font-bold text-center border-r border-slate-200 last:border-0">{day}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200">
                                            {turmas.map(turma => (
                                                <tr key={turma} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-3 font-bold text-slate-800 text-center border-r border-slate-200 bg-slate-50 sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">{turma}</td>
                                                    {weekdays.map(day => {
                                                        const prof = getProfessor('Community Time', day, turma);
                                                        const isHighlighted = selectedProfessor && prof === selectedProfessor;
                                                        return (
                                                            <td
                                                                key={`${turma}-${day}`}
                                                                className={`px-4 py-3 text-center border-r border-slate-200 last:border-0 whitespace-nowrap transition-colors
                              ${isHighlighted ? 'bg-indigo-100 text-indigo-800 font-bold ring-2 ring-indigo-500 ring-inset' :
                                                                        (prof === 'VOLANTE' ? 'font-bold text-slate-900' : 'font-medium text-slate-600')}
                            `}
                                                            >
                                                                {prof}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </section>

                            {/* Cycle Tests Table */}
                            <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-8">
                                <div className="bg-rose-600 px-6 py-4">
                                    <h3 className="text-sm sm:text-lg font-bold text-white flex items-center gap-2">
                                        <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5" />
                                        Cycle Tests (Sextas/Aplicação)
                                    </h3>
                                </div>
                                <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">

                                    {/* 6º e 7º */}
                                    <div className="overflow-x-auto">
                                        <div className="bg-slate-50 py-2 text-center border-b border-slate-200">
                                            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">6º E 7º</span>
                                        </div>
                                        <table className="w-full text-sm text-left">
                                            <thead className="text-xs text-slate-600 uppercase bg-slate-50 border-b border-slate-200">
                                                <tr>
                                                    <th className="px-6 py-3 font-bold border-r border-slate-200 w-24 text-center">Turma</th>
                                                    <th className="px-6 py-3 font-bold">Professor Titular</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200">
                                                {turmas67.map(turma => {
                                                    const prof = getProfessor('Cycle Tests', 'Dia de Teste', turma);
                                                    const isHighlighted = selectedProfessor && prof === selectedProfessor;
                                                    const isMissing = prof === '-';
                                                    return (
                                                        <tr key={turma} className="hover:bg-slate-50 transition-colors">
                                                            <td className="px-6 py-3 font-bold text-slate-800 border-r border-slate-200 text-center bg-slate-50">{turma}</td>
                                                            <td className={`px-6 py-3 whitespace-nowrap transition-colors
                            ${isHighlighted ? 'bg-indigo-100 text-indigo-800 font-bold ring-2 ring-indigo-500 ring-inset' :
                                                                    (isMissing ? 'text-rose-500 font-medium whitespace-break-spaces' :
                                                                        (prof === 'VOLANTE' ? 'font-bold text-slate-900' : 'font-medium text-slate-600'))}
                          `}>
                                                                {isMissing ? 'SEM DESIGNAÇÃO' : prof}
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* 8º e 9º */}
                                    <div className="overflow-x-auto">
                                        <div className="bg-slate-50 py-2 text-center border-b border-slate-200">
                                            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">8º E 9º</span>
                                        </div>
                                        <table className="w-full text-sm text-left">
                                            <thead className="text-xs text-slate-600 uppercase bg-slate-50 border-b border-slate-200">
                                                <tr>
                                                    <th className="px-6 py-3 font-bold border-r border-slate-200 w-24 text-center">Turma</th>
                                                    <th className="px-6 py-3 font-bold">Professor Titular</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200">
                                                {turmas89.map(turma => {
                                                    const prof = getProfessor('Cycle Tests', 'Dia de Teste', turma);
                                                    const isHighlighted = selectedProfessor && prof === selectedProfessor;
                                                    const isMissing = prof === '-';
                                                    return (
                                                        <tr key={turma} className="hover:bg-slate-50 transition-colors">
                                                            <td className="px-6 py-3 font-bold text-slate-800 border-r border-slate-200 text-center bg-slate-50">{turma}</td>
                                                            <td className={`px-6 py-3 whitespace-nowrap transition-colors
                            ${isHighlighted ? 'bg-indigo-100 text-indigo-800 font-bold ring-2 ring-indigo-500 ring-inset' :
                                                                    (isMissing ? 'text-rose-500 font-medium whitespace-break-spaces' :
                                                                        (prof === 'VOLANTE' ? 'font-bold text-slate-900' : 'font-medium text-slate-600'))}
                          `}>
                                                                {isMissing ? 'SEM DESIGNAÇÃO' : prof}
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </>)}
            </main>
        </div>
    );
}
